import React, { useEffect } from 'react';
import {
  LayoutDashboard,
  Paintbrush,
  Files,
  FileText,
  Anchor,
  CalendarCheck,
  CalendarDays,
  MessageSquare,
  Image as ImageIcon,
  Search,
  Globe,
  Settings2,
  CreditCard,
  Building2,
  Ship,
  Sparkles,
  ExternalLink,
  ShieldCheck,
  AlertCircle,
  Layers,
  X,
  Briefcase,
  MapPin,
  Activity,
  Clock,
  TrendingUp,
  Smartphone,
  CheckCircle2,
  Compass,
  Plus,
  Server,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { ShopierService } from '../../services/shopierService';
import { useAuth } from '../../context/AuthContext';
import { getSiteDisplayAddress } from '../../utils/security';

interface SidebarProps {
  currentTab?: string;
  currentView?: string;
  onSelectTab?: (tab: string) => void;
  onViewChange?: (view: string) => void;
  onOpenLiveSite?: () => void;
  onOpenWizard?: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentTab,
  currentView,
  onSelectTab,
  onViewChange,
  onOpenLiveSite,
  onOpenWizard,
  isMobileOpen = false,
  onCloseMobile,
}) => {
  const { activeSite, sites, setActiveSiteById, unreadCommunicationsCount = 0 } = useSite();
  const { currentUser } = useAuth();

  const active = currentTab || currentView || 'dashboard';
  const handleSelect = (tab: string) => {
    if (onSelectTab) onSelectTab(tab);
    if (onViewChange) onViewChange(tab);
    if (onCloseMobile) onCloseMobile();
  };

  // Listen for ESC key to close mobile drawer
  useEffect(() => {
    if (!isMobileOpen || !onCloseMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseMobile();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileOpen, onCloseMobile]);

  // Allow sidebar to render even when no activeSite is selected (e.g. new user)
  const isYacht = activeSite ? activeSite.industry === 'YACHT_RENTAL' : false;
  const subEval = activeSite?.subscription?.expireDate
    ? ShopierService.evaluateSubscriptionStatus(activeSite.subscription.expireDate)
    : { status: 'ACTIVE', daysRemaining: 3 };

  const sidebarContent = (
    <div className="flex flex-col justify-between h-full select-none overflow-y-auto">
      <div className="p-4 space-y-6">
        {/* Mobile Header with Close Button */}
        {isMobileOpen && (
          <div className="flex items-center justify-between pb-2 border-b border-slate-800 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold shadow-md shadow-blue-500/20">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-sm text-white">Adiosoft Panel</div>
                <div className="text-[10px] text-slate-400">Yönetim Menüsü</div>
              </div>
            </div>
            <button
              onClick={onCloseMobile}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
              aria-label="Menüyü Kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Active Site Header Info / Empty Site Card */}
        {activeSite ? (
          <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {isYacht ? (
                  <div className="w-6 h-6 rounded-md bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                    <Ship className="w-3.5 h-3.5" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-md bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                    <Building2 className="w-3.5 h-3.5" />
                  </div>
                )}
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300">
                  {isYacht ? 'Yat Kiralama Paneli' : 'Kurumsal Panel'}
                </span>
              </div>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  activeSite.status === 'PUBLISHED'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                }`}
              >
                {activeSite.status === 'PUBLISHED' ? 'Yayında' : 'Taslak'}
              </span>
            </div>

            <div className="font-bold text-sm text-white truncate">{activeSite.name}</div>
            <div className="text-[11px] text-slate-400 truncate mt-0.5">{getSiteDisplayAddress(activeSite)}</div>

            {/* Quick Live Preview link */}
            {onOpenLiveSite && (
              <button
                onClick={() => {
                  if (onCloseMobile) onCloseMobile();
                  onOpenLiveSite();
                }}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium transition"
              >
                <ExternalLink className="w-3 h-3" />
                Canlı Siteyi Önizle
              </button>
            )}
          </div>
        ) : (
          <div className="p-3.5 rounded-xl bg-slate-800/90 border border-blue-500/30 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-blue-500/20 text-blue-400 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300">
                  Adiosoft Panel
                </span>
              </div>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                0 Site
              </span>
            </div>

            <div className="font-bold text-sm text-white">Henüz Site Yok</div>
            <div className="text-[11px] text-slate-400 mt-0.5">3 günlük deneme hakkınız hazır</div>

            <button
              onClick={() => {
                if (onCloseMobile) onCloseMobile();
                if (onOpenWizard) onOpenWizard();
                else handleSelect('sites');
              }}
              className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition shadow-sm shadow-blue-600/20"
            >
              <Plus className="w-3.5 h-3.5" />
              Web Sitesi Oluştur
            </button>
          </div>
        )}

          {/* MENU SECTIONS */}
          {/* 1. GENEL BAKIŞ VE CANLI DURUM */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
              Genel & Durum
            </div>

            <button
              id="sidebar-tab-dashboard"
              onClick={() => handleSelect('dashboard')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'dashboard'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 text-blue-400" />
              Genel Bakış
            </button>

            <button
              id="sidebar-tab-site-status"
              onClick={() => handleSelect('site-status')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'site-status'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Activity className="w-4 h-4 text-emerald-400" />
                <span className="font-semibold">SİTE DURUMU</span>
              </div>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </button>

            <button
              id="sidebar-tab-sites"
              onClick={() => handleSelect('sites')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'sites'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4 text-sky-400" />
              Sitelerim (Tüm Projeler)
            </button>
          </div>

          {/* 2. BÜYÜME & AI GÖRÜNÜRLÜK */}
          <div className="space-y-1 pt-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
              Büyüme & Pazarlama
            </div>

            <button
              id="sidebar-tab-ai-visibility"
              onClick={() => handleSelect('ai-visibility')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition cursor-pointer ${
                active === 'ai-visibility'
                  ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-indigo-400" />
                <span>AI Yerel Görünürlük</span>
              </div>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-semibold">
                Yeni
              </span>
            </button>

            <button
              id="sidebar-tab-subscriptions"
              onClick={() => handleSelect('subscriptions')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition cursor-pointer ${
                active === 'subscriptions'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <CreditCard className="w-4 h-4 text-amber-400" />
              <span>Aboneliklerim & Geçmiş</span>
            </button>
          </div>

          {/* 3. WEBSITE YÖNETİMİ & EDİTÖR */}
          <div className="space-y-1 pt-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
              Website & Görsel Editör
            </div>

            <button
              id="sidebar-tab-editor"
              onClick={() => handleSelect('editor')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'editor'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Paintbrush className="w-4 h-4 text-indigo-400" />
              Görsel Editör (Canlı)
            </button>
          </div>

          {/* CORPORATE SPECIFIC MODULES */}
          {!isYacht && (
            <div className="space-y-1 pt-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
                İçerik & Tasarım Sistemi
              </div>

              <button
                id="sidebar-tab-business-profile"
                onClick={() => handleSelect('business-profile')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'business-profile'
                    ? 'bg-blue-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Building2 className="w-4 h-4 text-blue-400" />
                Firma Bilgileri (Merkezi)
              </button>

              <button
                id="sidebar-tab-design-system"
                onClick={() => handleSelect('design-system')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'design-system'
                    ? 'bg-blue-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Sparkles className="w-4 h-4 text-purple-400" />
                Tasarım & Varyasyonlar
              </button>

              <button
                id="sidebar-tab-testimonials"
                onClick={() => handleSelect('testimonials')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'testimonials'
                    ? 'bg-blue-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <MessageSquare className="w-4 h-4 text-amber-400" />
                Referanslar & Yorumlar
              </button>
            </div>
          )}

          {/* YACHT RENTAL SPECIFIC MODULES */}
          {isYacht && (
            <div className="space-y-1 pt-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 px-3 py-1">
                Yat & Rezervasyon
              </div>

              <button
                id="sidebar-tab-yachts"
                onClick={() => handleSelect('yachts')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'yachts'
                    ? 'bg-cyan-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Anchor className="w-4 h-4 text-cyan-400" />
                Yat Yönetimi (Filo)
              </button>

              <button
                id="sidebar-tab-reservations"
                onClick={() => handleSelect('reservations')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'reservations'
                    ? 'bg-cyan-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <CalendarCheck className="w-4 h-4 text-cyan-400" />
                Rezervasyon Talepleri
              </button>

              <button
                id="sidebar-tab-calendar"
                onClick={() => handleSelect('calendar')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'calendar'
                    ? 'bg-cyan-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <CalendarDays className="w-4 h-4 text-cyan-400" />
                Müsaitlik Takvimi
              </button>

              <button
                id="sidebar-tab-routes"
                onClick={() => handleSelect('routes')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'routes'
                    ? 'bg-cyan-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Compass className="w-4 h-4 text-cyan-400" />
                Mavi Tur Rotaları
              </button>

              <button
                id="sidebar-tab-marinas"
                onClick={() => handleSelect('marinas')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                  active === 'marinas'
                    ? 'bg-cyan-600 text-white shadow-xs font-semibold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Ship className="w-4 h-4 text-cyan-400" />
                Marinalar & Limanlar
              </button>
            </div>
          )}

          {/* MEDYA VE İLETİŞİM */}
          <div className="space-y-1 pt-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
              Medya & İletişim
            </div>

            <button
              id="sidebar-tab-communications"
              onClick={() => handleSelect('communications')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'communications'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-teal-400" />
                <span>Medya ve İletişim</span>
              </div>
              {unreadCommunicationsCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-500 text-white animate-pulse">
                  {unreadCommunicationsCount}
                </span>
              )}
            </button>

            <button
              id="sidebar-tab-media"
              onClick={() => handleSelect('media')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'media'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <ImageIcon className="w-4 h-4 text-purple-400" />
              Medya Kütüphanesi
            </button>
          </div>

          {/* 3. SEO, DOMAIN & AYARLAR */}
          <div className="space-y-1 pt-2">
            <button
              id="sidebar-tab-seo"
              onClick={() => handleSelect('seo')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'seo'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Search className="w-4 h-4 text-pink-400" />
              <span>SEO</span>
            </button>

            <button
              id="sidebar-tab-domains"
              onClick={() => handleSelect('domains')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'domains'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Globe className="w-4 h-4 text-cyan-400" />
              <span>Domain</span>
            </button>

            <button
              id="sidebar-tab-settings"
              onClick={() => handleSelect('settings')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                active === 'settings'
                  ? 'bg-blue-600 text-white shadow-xs font-semibold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Settings2 className="w-4 h-4 text-slate-400" />
              <span>Ayarlar</span>
            </button>

            {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'super_admin') && (
              <button
                id="sidebar-tab-admin"
                type="button"
                onClick={() => {
                  window.history.pushState({}, '', '/admin');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition text-rose-300 hover:bg-rose-950/40 hover:text-rose-200 border border-rose-800/40"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-4 h-4 text-rose-400" />
                  <span className="font-bold">Şirket Yönetim Paneli</span>
                </div>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border border-rose-500/30 font-bold uppercase">
                  /admin
                </span>
              </button>
            )}
          </div>
      </div>

      {/* Bottom Subscription status card */}
      <div className="p-4 border-t border-slate-800 bg-slate-950/40">
        <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/60 text-xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-slate-400">Abonelik Durumu</span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                subEval.status === 'ACTIVE'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : subEval.status === 'EXPIRING_SOON'
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'bg-rose-500/20 text-rose-300'
              }`}
            >
              {subEval.status === 'ACTIVE'
                ? 'Aktif'
                : subEval.status === 'EXPIRING_SOON'
                ? 'Sona Eriyor'
                : 'Süresi Doldu'}
            </span>
          </div>
          <div className="font-medium text-slate-200">
            {activeSite?.subscription?.planName || '3 Günlük Deneme Paketi'}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {subEval.daysRemaining > 0
              ? `${subEval.daysRemaining} gün kaldı`
              : 'Yenileme gerekli'}
          </div>

          <button
            onClick={() => handleSelect('billing')}
            className="mt-2.5 w-full py-1.5 px-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-[11px] font-semibold transition flex items-center justify-center gap-1.5"
          >
            <CreditCard className="w-3 h-3" />
            Shopier ile Yönet
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* 1. Desktop Persistent Sidebar */}
      <aside className="hidden lg:flex w-64 bg-slate-900 border-r border-slate-800 flex-col shrink-0 min-h-[calc(100vh-4rem)]">
        {sidebarContent}
      </aside>

      {/* 2. Mobile / Tablet Slide-In Drawer */}
      {isMobileOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Yönetim Menüsü"
          className="fixed inset-0 z-50 lg:hidden animate-in fade-in duration-200"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-950/75 backdrop-blur-sm transition-opacity"
            onClick={onCloseMobile}
            aria-hidden="true"
          />

          {/* Drawer Container */}
          <aside className="relative z-10 w-72 sm:w-80 max-w-[85vw] bg-slate-900 h-full border-r border-slate-800 shadow-2xl flex flex-col animate-in slide-in-from-left duration-300">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};

